import { useRef, useState, useEffect, useMemo } from "react";
import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ScrollableContent,
  ActionSection,
  MobileIcon,
  CombineInputGroup,
  CardWrapper,
  SelectionCard,
  ModalHeader,
} from "../Styles/InputStyles";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit } from "react-icons/md";
import * as Yup from "yup";
import { enquirySourceOptions, propertyFeatures } from "../../Config";
import SelectOptionModal from "../SelectOptionModal";
import { useSelector } from "react-redux";
import SelectLocationModal from "../SelectLocationModal";
import { FormControl, MenuItem, Select } from "@mui/material";
import SelectPreferenceModal from "../SelectPreferenceModal";
import {
  furnishedOptions,
  preferredTenantOptions,
  rentRange,
} from "../../helpers";

const AddEnquiry = ({
  addEnquiriesLoading,
  addEnquiryMutate,
  setIsAddEnquiries,
  key,
}) => {
  const [height, setHeight] = useState(window.innerHeight);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  let formikRef = useRef(null);

  const [showLocationModal, setShowLocationModal] = useState(false);

  const locations = useSelector((state) => state.LocationsReducer.locations);

  const filteredLocations = useMemo(
    () => locations?.filter((l) => l.id !== 0) || [],
    [locations],
  );

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    const handleResize = () => {
      setHeight(window.innerHeight); // updates when Safari UI changes
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [key]);

  const initialValues = {
    fullName: "",
    mobile: "",
    locationId: "",
    rentRange: "",
    source: "",
    preferences: [],
    furnishedType: "",
    preferredTenantType: "",
    notes: "",
  };

  const validationSchema = Yup.object({
    fullName: Yup.string()
      .required("Name is required")
      .matches(
        /^[A-Za-z0-9.\s]+$/,
        "Only letters, numbers, spaces and dot are allowed.",
      )
      .min(4, "Name must be at least 4 characters long"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),

    locationId: Yup.string().required("Location is required"),
    rentRange: Yup.string().required("Rent range is required"),
    source: Yup.number().required("Source is required"),
    furnishedType: Yup.string().nullable(),
    preferredTenantType: Yup.string().nullable(),
    // preferences: Yup.array()
    //   .min(1, "At least one preference is required")
    //   .required("Preference is required"),
  });

  const handleSubmit = (values, { resetForm }) => {
    addEnquiryMutate({
      name: values.fullName,
      mobile: values.mobile,
      locationId: values.locationId,
      rentRange: values.rentRange || "",
      source: values.source || "",
      preferences: JSON.stringify(values.preferences || []),
      tenantPreference: parseInt(values.preferredTenantType || ""),
      furnishingType: parseInt(values.furnishedType || ""),
      note: values.notes || "",
    });
    //resetForm();
  };

  return (
    <Container
      style={{
        height: `${height}px`,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <ModalHeader>
        <h1 className="text-white text-lg font-medium">Add Enquiry</h1>
        <button onClick={() => setIsAddEnquiries(false)}>
          <MdClose color="white" size={24} />
        </button>
      </ModalHeader>

      <div className="px-6 h-full flex flex-col justify-between">
        <Formik
          initialValues={initialValues}
          innerRef={formikRef}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, resetForm, errors, touched }) => {
            const getSelectedLabels = () => {
              if (!values.preferences?.length) {
                return "Select Preference";
              }

              const selected = propertyFeatures.filter((feature) =>
                values.preferences.includes(feature.value),
              );

              return selected.map((f) => f.label).join(", ");
            };

            const getSelectedSource = () => {
              if (!values.source) return "Select Source";

              const selected = enquirySourceOptions.find(
                (s) => s.value === values.source,
              );

              return selected ? selected.label : "Select Source";
            };

            const handleFeaturesSelect = (selectedValues) => {
              setFieldValue("preferences", selectedValues);
            };

            const getLocationLabel = () => {
              const selectedLocation = filteredLocations.find(
                (loc) => loc.id === values.locationId,
              );
              return selectedLocation
                ? selectedLocation.name
                : "Select Location";
            };

            return (
              <Form>
                <ScrollableContent className="max-h-[calc(100vh-140px)]">
                  <Wrapper>
                    <InputGroup>
                      <Label htmlFor="fullName">Name *</Label>
                      <FieldWrappers>
                        <Field
                          type="text"
                          name="fullName"
                          id="fullName"
                          autoComplete="off"
                          placeholder="Enter name"
                        />
                      </FieldWrappers>
                      {errors.fullName && touched.fullName && (
                        <ErrorText>{errors.fullName}</ErrorText>
                      )}
                    </InputGroup>
                    <InputGroup>
                      <Label htmlFor="mobile">Mobile Number *</Label>
                      <FieldWrappers>
                        <MobileIcon />
                        <Field
                          type="text"
                          name="mobile"
                          id="mobile"
                          inputMode="numeric"
                          autoComplete="off"
                          className="pl-5"
                          placeholder="Enter 10-digit mobile number"
                          maxLength={10}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d{0,10}$/.test(value)) {
                              setFieldValue("mobile", value);
                            }
                          }}
                        />
                      </FieldWrappers>
                      {errors.mobile && touched.mobile && (
                        <ErrorText>{errors.mobile}</ErrorText>
                      )}
                    </InputGroup>

                    <CombineInputGroup>
                      <InputGroup>
                        <Label htmlFor="rentRange">Rent Range *</Label>
                        <FormControl fullWidth>
                          <Select
                            className="px-2"
                            value={values.rentRange}
                            onChange={(e) =>
                              setFieldValue("rentRange", e.target.value)
                            }
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return (
                                  <span className="text-gray-400">
                                    Select rent range
                                  </span>
                                );
                              }
                              const option = rentRange.find(
                                (opt) => opt.value === selected,
                              );
                              return option ? option.label : selected;
                            }}
                            sx={{ height: "46px" }}
                          >
                            <MenuItem value="" disabled>
                              <span className="text-gray-400">
                                Select Rentrange
                              </span>
                            </MenuItem>
                            {rentRange.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {touched.rentRange && errors.rentRange && (
                          <p className="text-rose-600 text-sm mt-1">
                            {errors.rentRange}
                          </p>
                        )}
                      </InputGroup>
                    </CombineInputGroup>

                    <CombineInputGroup>
                      <InputGroup>
                        <Label>Select Location *</Label>
                        <FieldWrappers
                          onClick={() => setShowLocationModal(true)}
                        >
                          <Field
                            readOnly
                            value={getLocationLabel()}
                            className="w-full bg-transparent border-none outline-none cursor-pointer"
                          />
                          <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                        </FieldWrappers>
                        {touched.locationId && errors.locationId && (
                          <div className="min-h-[20px] mt-1">
                            <p className="text-rose-600 text-sm">
                              {errors.locationId}
                            </p>
                          </div>
                        )}
                      </InputGroup>

                      <InputGroup>
                        <Label>Source *</Label>

                        <FieldWrappers
                          onClick={() => setShowSourceModal(true)}
                          // className="border border-gray-400"
                        >
                          <Field
                            type="text"
                            readOnly
                            value={getSelectedSource()}
                            className="w-full bg-transparent border-none outline-none cursor-pointer"
                          />

                          <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                        </FieldWrappers>
                        {touched.source && errors.source && (
                          <div className="min-h-[20px] mt-1">
                            <p className="text-rose-600 text-sm">
                              {errors.source}
                            </p>
                          </div>
                        )}
                      </InputGroup>
                    </CombineInputGroup>
                    <InputGroup>
                      <Label>Preference</Label>

                      <FieldWrappers
                        onClick={() => setShowFeaturesModal(true)}
                        // className="border border-gray-400"
                      >
                        <Field
                          type="text"
                          readOnly
                          value={getSelectedLabels()}
                          className="w-full border-none outline-none bg-transparent cursor-pointer"
                        />

                        <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                      </FieldWrappers>

                      {touched.preferences && errors.preferences && (
                        <p className="text-rose-600 text-sm mt-1">
                          {errors.preferences}
                        </p>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Furnishing</Label>
                      <CardWrapper>
                        {furnishedOptions.map((option) => (
                          <SelectionCard
                            key={option.value}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setFieldValue("furnishedType", option.value);
                            }}
                            className={
                              values.furnishedType === option.value
                                ? "bg-[#26A69A] text-white"
                                : "bg-gray-50 text-gray-800"
                            }
                          >
                            <span className="text-sm">{option.label}</span>
                          </SelectionCard>
                        ))}
                      </CardWrapper>
                      {touched.furnishedType && errors.furnishedType && (
                        <p className="text-rose-600 text-sm mt-1">
                          {errors.furnishedType}
                        </p>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Tenant Type</Label>
                      <CardWrapper>
                        {preferredTenantOptions.map((option) => (
                          <SelectionCard
                            key={option.value}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setFieldValue(
                                "preferredTenantType",
                                option.value,
                              );
                            }}
                            className={
                              values.preferredTenantType === option.value
                                ? "bg-[#26A69A] text-white"
                                : "bg-gray-50 text-gray-800"
                            }
                          >
                            <span className="text-sm">{option.label}</span>
                          </SelectionCard>
                        ))}
                      </CardWrapper>
                      {touched.preferredTenantType &&
                        errors.preferredTenantType && (
                          <p className="text-rose-600 text-sm mt-1">
                            {errors.preferredTenantType}
                          </p>
                        )}
                    </InputGroup>
                    <InputGroup>
                      <Label htmlFor="notes">Notes</Label>
                      <FieldWrapper>
                        <Field
                          as={Textarea}
                          id="notes"
                          name="notes"
                          rows={3}
                          maxLength={500}
                          placeholder="Write your notes here..."
                        />
                      </FieldWrapper>
                    </InputGroup>
                  </Wrapper>
                </ScrollableContent>
                <ActionSection>
                  <SideCancelBtn
                    type="button"
                    onClick={() => {
                      setIsAddEnquiries(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </SideCancelBtn>
                  <SubmitBtn type="submit" disabled={addEnquiriesLoading}>
                    {addEnquiriesLoading ? "Submiting..." : "Submit"}
                  </SubmitBtn>
                </ActionSection>

                <SelectPreferenceModal
                  isOpen={showFeaturesModal}
                  onClose={() => setShowFeaturesModal(false)}
                  options={propertyFeatures}
                  selectedValue={values.preferences}
                  onSelect={handleFeaturesSelect}
                  title="Select Preference"
                  searchPlaceholder="Search Preference..."
                />
                <SelectOptionModal
                  isOpen={showSourceModal}
                  onClose={() => setShowSourceModal(false)}
                  title="Select Source"
                  options={enquirySourceOptions}
                  selectedValue={values.source}
                  onSelect={(val) => {
                    setFieldValue("source", Number(val));
                    setShowSourceModal(false);
                  }}
                  searchPlaceholder="Search Source..."
                />

                <SelectLocationModal
                  isOpen={showLocationModal}
                  onClose={() => setShowLocationModal(false)}
                  options={filteredLocations}
                  selectedValue={values.locationId}
                  onSelect={(val) => {
                    setFieldValue("locationId", val);
                    setShowLocationModal(false);
                  }}
                />
              </Form>
            );
          }}
        </Formik>
      </div>
    </Container>
  );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
const FieldWrapper = tw.div`w-full`;
const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y  break-words overflow-hidden`;

export default AddEnquiry;
